import { useMemo, useCallback, useEffect } from "react"
import PropTypes from "prop-types"
import moment from "moment"
import { chain, merge, get, map, identity, noop } from "lodash"
import { useQueryStringState } from "hooks/query-string"
import { useSaveContextPreferences } from "hooks/preferences"
import { useOutletContext } from "react-router-dom"
import { Paper } from "@mui/material"
import { OK } from "helpers/data-statuses"
import CompareSelect from "components/material/compare-select"
import CompareChart from "components/charts/compare"
import { percentageFormatter } from "helpers/formatters/numbers"
import { DATE } from "helpers/shapes"
import { COLORS as ANALYZE_COLORS } from "helpers/analyzeConstants"
import { ITEMS_COLORS } from "helpers/comparison"
import multiDateContract from "gatherers/multi-date/contract"
import useFilteredMetrics from "../../hooks/filtered-metrics"
import useDownloadData from "../../hooks/download-data"
import CompareGraphStatus from "./loading"

import "./style.scss"

const COLORS = [...ITEMS_COLORS, ...ANALYZE_COLORS]

function getMetricId(availableMetrics, optionId) {
  return chain(availableMetrics)
    .map(({ options }) => options)
    .flatten()
    .keyBy("id")
    .get([optionId, "id"])
    .value()
}

function indexedObjectToArray(values, iteratee = identity) {
  const itemValues = new Array(Object.keys(values).length).fill(null)

  chain(values)
    .toPairs()
    .forEach(([index, value]) => {
      itemValues[index] = value != null ? iteratee(value) : null
    })
    .value()

  return itemValues
}

// We use selectedMetric that's coming from the props and not directly the queryString because there is logic at the template level to make
// sure that the metric applies to the current data
export default function CompareGraph(props) {
  const { setDownloadData = noop, setDownloadNameChunk = noop } = useOutletContext()
  const { status, configKey, data: allPositionSetsTimeSeries, formatter, tickFormatter, availableMetrics, positionSetsMetadata, selectedMetric, downloadName, chartMargins, index: sectionIndex, defaultUndefinedValue } = props

  const [{ selectedDate }, updateQueryString] = useQueryStringState()
  const saveSelectedMetricPreference = useSaveContextPreferences("selectedMetric")
  // Transform the query data items into a format that the CompareChart component expects
  const comparedPositionSets = useMemo(() => map(positionSetsMetadata, (positionSet, index) => {
    const { name: label, unavailable } = positionSet
    // There are multiple cases where the position set can be marked as unavailable but its only when there is no label that its "Not Found"
    const isNotFound = !label && unavailable
    return ({
      id: `${index}`,
      label: isNotFound ? "Not Found" : label,
      disabled: unavailable,
      color: COLORS[index],
      unavailable,
    })
  }), [positionSetsMetadata])

  const filteredMetrics = useFilteredMetrics(availableMetrics, allPositionSetsTimeSeries)

  useEffect(() => {
    setDownloadNameChunk("metric", selectedMetric.label)
    return () => setDownloadNameChunk("metric", null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMetric?.id, setDownloadNameChunk])

  // Combine all positionSets dates into a format that CompareChart component expects
  // [{date: moment, [ps1]: { ...metricsValues }, [ps2]: ...metricsValues, ...}, ...]
  const graphLines = useMemo(() => {
    return chain(allPositionSetsTimeSeries)
      .map((timeSeries, index) => chain(timeSeries.values.dates)
        .keyBy(datum => datum.date)
        // Instead of having { [date]: { ...metricsValues } }, we want { [date]: { [positionIndex]: selectedMetricValue } } to allow for an easy merge of all positionSets
        .mapValues((date) => ({ [index]: get(date, selectedMetric?.id, defaultUndefinedValue) }))
        .value(),
      )
      .thru(positionSets => merge({}, ...positionSets))
      .map((dates, date) => ({
        date: moment(date, "YYYY-MM-DD"),
        ...dates,
      }))
      .sortBy("date")
      .value()
  }, [allPositionSetsTimeSeries, selectedMetric?.id, defaultUndefinedValue])

  const metricSelector = <CompareSelect
    options={filteredMetrics}
    selectedOption={selectedMetric}
    setSelected={(optionId) => {
      const selectedMetric = { view: configKey, id: getMetricId(filteredMetrics, optionId) }
      updateQueryString({ selectedMetric })
      saveSelectedMetricPreference(selectedMetric)
    }}
  />

  const generateHeaders = useCallback(() => ["Date", ...map(positionSetsMetadata, "name")], [positionSetsMetadata])
  const generateRows = useCallback(() => graphLines.map(({ date, ...values }) =>
    [date.format("YYYY-MM-DD"), ...indexedObjectToArray(values, formatter)],
  ), [graphLines, formatter])

  useDownloadData({
    configKey,
    sectionIndex,
    name: downloadName || `${selectedMetric?.label} Chart`,
    setDownloadData,
    generateHeaders,
    generateRows,
  })

  return <Paper square elevation={2} className="renderers_compare-graph">
    <CompareGraphStatus status={status} />
    {status === OK
      && <CompareChart
        {...{
          tickFormat: tickFormatter,
          date: selectedDate,
          data: graphLines,
          items: comparedPositionSets,
          metricSelector,
          tooltipFormatter: formatter,
          fixedItemNumber: false,
          margins: chartMargins,
          percents: formatter === percentageFormatter,
          setDate: (date) => updateQueryString({ selectedDate: date }),
          noDownload: true,
        }}
      />}
  </Paper>
}

CompareGraph.propTypes = {
  data: multiDateContract.isRequired,
  tickFormatter: PropTypes.func.isRequired,
  formatter: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  configKey: PropTypes.string.isRequired,
  availableMetrics: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })).isRequired,
  })).isRequired,
  selectedMetric: PropTypes.shape({
    view: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
  }).isRequired,
  positionSetsMetadata: PropTypes.arrayOf(PropTypes.shape({
    earliestDate: DATE.isRequired,
    latestDate: DATE,
    name: PropTypes.string.isRequired,
    unavailable: PropTypes.bool,
    fxHedgeEnabled: PropTypes.bool.isRequired,
  })).isRequired,
  selectedDate: DATE.isRequired,
  status: PropTypes.string.isRequired,
  downloadName: PropTypes.string,
  chartMargins: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number,
  }),
  index: PropTypes.number.isRequired,
  defaultUndefinedValue: PropTypes.number,
}
